import { Controller } from "@hotwired/stimulus"

const HIDE_DELAY = 120

export default class extends Controller {
  static targets = ["trigger", "bubble"]
  static values = { placement: String }

  connect() {
    this._onDocPointer = (e) => { if (!this.element.contains(e.target)) this.hide() }
    this._onKeydown = (e) => { if (e.key === "Escape") this.hide() }
    this._reposition = () => { if (this.shown) this.position() }
    this._onBubbleEnter = (e) => { if (e.pointerType === "mouse") this._cancelHide() }
    this._onBubbleLeave = (e) => { if (e.pointerType === "mouse" && !this.pinned) this._scheduleHide() }
    this.bubbleTarget.addEventListener("pointerenter", this._onBubbleEnter)
    this.bubbleTarget.addEventListener("pointerleave", this._onBubbleLeave)
  }

  disconnect() {
    this.bubbleTarget.removeEventListener("pointerenter", this._onBubbleEnter)
    this.bubbleTarget.removeEventListener("pointerleave", this._onBubbleLeave)
    this._cancelHide()
    this._teardown()
  }

  pointerEnter(event) {
    if (event.pointerType !== "mouse") return
    this._cancelHide()
    this.show()
  }

  pointerLeave(event) {
    if (event.pointerType === "mouse" && !this.pinned) this._scheduleHide()
  }

  focusOut(event) {
    if (this.element.contains(event.relatedTarget) || this.bubbleTarget.matches(":hover")) return
    this.hide()
  }

  toggle(event) {
    event.preventDefault()
    if (this.pinned) {
      this.hide()
    } else {
      this._cancelHide()
      this.show()
      this.pinned = true
    }
  }

  show() {
    if (this.shown) return
    this.bubbleTarget.classList.remove("hidden")
    this.shown = true
    this.position()
    document.addEventListener("pointerdown", this._onDocPointer, true)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("scroll", this._reposition, true)
    window.addEventListener("resize", this._reposition)
  }

  hide() {
    this._cancelHide()
    this.pinned = false
    if (!this.shown) return
    this.bubbleTarget.classList.add("hidden")
    this.shown = false
    this._teardown()
  }

  position() {
    const t = this.triggerTarget.getBoundingClientRect()
    const b = this.bubbleTarget
    b.style.left = "0px"
    b.style.top = "0px"
    const pad = 8
    const vw = document.documentElement.clientWidth
    const w = b.offsetWidth
    let left = Math.min(t.left, vw - w - pad)
    left = Math.max(pad, left)
    let ox = 0
    let oy = 0
    const cb = this.containingBlock()
    if (cb) {
      const r = cb.getBoundingClientRect()
      ox = r.left
      oy = r.top
    }
    const h = b.offsetHeight
    const vh = document.documentElement.clientHeight
    const above = t.top - h - 6
    const below = t.bottom + 6
    const fitsAbove = above >= pad
    const fitsBelow = below + h <= vh - pad
    let top = this.placementValue === "top" ? above : below
    if (top === above && !fitsAbove && (fitsBelow || vh - t.bottom > t.top)) top = below
    else if (top === below && !fitsBelow && (fitsAbove || t.top > vh - t.bottom)) top = above
    b.style.top = `${Math.round(top - oy)}px`
    b.style.left = `${Math.round(left - ox)}px`
  }

  containingBlock() {
    let el = this.bubbleTarget.parentElement
    while (el && el !== document.documentElement) {
      const s = getComputedStyle(el)
      if (s.transform !== "none" || s.perspective !== "none" || s.filter !== "none") return el
      el = el.parentElement
    }
    return null
  }

  _scheduleHide() {
    this._cancelHide()
    this._hideTimer = setTimeout(() => this.hide(), HIDE_DELAY)
  }

  _cancelHide() {
    clearTimeout(this._hideTimer)
    this._hideTimer = null
  }

  _teardown() {
    document.removeEventListener("pointerdown", this._onDocPointer, true)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("scroll", this._reposition, true)
    window.removeEventListener("resize", this._reposition)
  }
}
